
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { Shield, FileText, Cookie, Lock, Scale, CheckCircle2, ChevronRight, FileCheck, Landmark, ShieldCheck, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

interface LegalPageProps {
    onLogin: () => void;
    onNavigate: (page: string) => void;
    onNavigateToDocs: (section?: string) => void;
}

const LegalSection = ({ title, icon: Icon, children, id }: { title: string, icon: any, children?: React.ReactNode, id: string }) => (
    <section id={id} className="mb-16 md:mb-32 scroll-mt-32 group px-0">
        <div className="flex items-center gap-5 mb-10 border-b border-stone-100 dark:border-white/5 pb-8">
            <div className="p-4 bg-[#0A0B0F] dark:bg-white rounded-sm text-nobel-gold dark:text-[#0A0B0F] group-hover:scale-110 transition-all duration-500 shadow-xl shrink-0">
                <Icon size={28} />
            </div>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#0A0B0F] dark:text-white tracking-tight">{title}</h2>
        </div>
        <div className="prose dark:prose-invert max-w-none text-stone-600 dark:text-stone-300 leading-relaxed font-light text-base md:text-lg space-y-6">
            {children}
        </div>
    </section>
);

const AuditLine = ({ title, status }: { title: string, status: string }) => (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between py-5 border-b border-white/10 last:border-0 gap-2 sm:gap-4 group cursor-default hover:bg-white/5 transition-colors px-4 -mx-4 rounded-lg">
        <span className="text-stone-400 font-black text-[10px] uppercase tracking-[0.3em]">{title}</span>
        <div className="flex items-center gap-2.5">
            <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_12px_#10b981] animate-pulse"></div>
            <span className="text-emerald-400 font-mono text-xs font-bold uppercase tracking-widest">{status}</span>
        </div>
    </div>
);

export const LegalPage: React.FC<LegalPageProps> = ({ onLogin, onNavigate, onNavigateToDocs }) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="bg-white dark:bg-[#0A0B0F] min-h-screen font-sans text-[#0A0B0F] dark:text-white selection:bg-nobel-gold selection:text-black">
            <Header onLogin={onLogin} onNavigateToDocs={onNavigateToDocs} onNavigate={(p) => onNavigate(p as any)} />
            
            <main className="pt-24 md:pt-32 pb-32 relative">
                <div className="absolute top-0 inset-x-0 h-[600px] bg-gradient-to-b from-stone-50 dark:from-white/[0.02] to-transparent"></div>
                <div className="absolute top-40 right-0 w-[600px] h-[600px] bg-nobel-gold/5 blur-[120px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/2"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="max-w-6xl mx-auto">
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-20 md:mb-32 text-center"
                        >
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stone-100 dark:bg-white/5 text-[10px] font-black uppercase tracking-[0.4em] text-stone-500 border border-stone-200 dark:border-white/10 mb-8 shadow-sm">
                                Institutional Trust Center
                            </div>
                            <h1 className="font-serif text-5xl md:text-8xl font-bold mb-8 text-[#0A0B0F] dark:text-white tracking-tighter leading-tight">
                                Legal <br /> <span className="italic text-stone-400 dark:text-stone-500 font-light text-6xl md:text-8xl">Foundation.</span>
                            </h1>
                            <p className="text-lg md:text-2xl text-stone-500 dark:text-stone-400 font-light max-w-2xl mx-auto px-4 leading-relaxed">
                                Transparency, privacy, and cryptographically-enforced compliance frameworks for the global sovereign economy.
                            </p>
                        </motion.div>

                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 md:gap-20">
                            {/* Sidebar Navigation - Stacks vertically and becomes a row of buttons on mobile */}
                            <div className="col-span-1">
                                <div className="lg:sticky lg:top-32 space-y-1">
                                    <div className="text-[10px] font-black text-stone-400 uppercase tracking-[0.3em] mb-6 px-4 hidden lg:block">Framework Sections</div>
                                    <div className="flex flex-col gap-2">
                                        {[
                                            { id: 'terms', label: 'Terms of Protocol', icon: Scale },
                                            { id: 'privacy', label: 'Privacy Standards', icon: Lock },
                                            { id: 'cookies', label: 'Cookie Policy', icon: Cookie },
                                            { id: 'compliance', label: 'Audit Registry', icon: FileCheck }
                                        ].map((link) => (
                                            <a 
                                                key={link.id}
                                                href={`#${link.id}`} 
                                                className="flex items-center justify-between gap-3 px-6 py-5 text-xs font-black uppercase tracking-widest text-stone-500 dark:text-stone-400 hover:text-nobel-gold hover:bg-[#0A0B0F] dark:hover:bg-white hover:text-white dark:hover:text-[#0A0B0F] rounded-sm transition-all group border border-stone-100 dark:border-white/10 lg:border-none shadow-sm lg:shadow-none bg-white dark:bg-white/5 lg:bg-transparent"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <link.icon size={18} className="opacity-40 group-hover:opacity-100 shrink-0" />
                                                    {link.label}
                                                </div>
                                                <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </a>
                                        ))}
                                    </div>
                                    <div className="mt-12 pt-8 border-t border-stone-100 dark:border-white/10 px-4 hidden lg:block">
                                        <div className="text-[9px] font-black text-stone-400 uppercase tracking-[0.4em] mb-3">Technical Contact</div>
                                        <a href="mailto:ops@ghostpay.fun" className="text-xs font-mono font-bold text-nobel-gold hover:underline">ops@ghostpay.fun</a>
                                    </div>
                                </div>
                            </div>

                            {/* Content Area */}
                            <div className="col-span-1 lg:col-span-3 space-y-20">
                                <LegalSection id="terms" title="Terms of Protocol" icon={Scale}>
                                    <div className="flex items-center gap-2 text-[10px] font-black text-stone-400 uppercase tracking-[0.4em] mb-10">
                                        <div className="w-8 h-px bg-stone-200 dark:bg-white/10"></div>
                                        Effective: JAN 2026
                                    </div>
                                    <p>
                                        GhostPay operates as a high-performance decentralized interface for the Ghost Network. By utilizing our services, you interact with the GhostOS runtime, agreeing to the cryptographic protocols of the sovereign digital jurisdiction.
                                    </p>
                                    <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#0A0B0F] dark:text-white mt-16 mb-6">Non-Custodial Architecture</h3>
                                    <p>
                                        GhostPay does not hold, custody, or manage user funds. All transactions occur directly on the Solana or Sui blockchains via secure, isolated programs. Users maintain exclusive control over private keys.
                                    </p>
                                    <div className="my-12 p-8 bg-stone-900 dark:bg-white/[0.03] rounded-sm border border-white/10 flex flex-col items-start gap-6 shadow-2xl relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12"><ShieldCheck size={120} className="text-white"/></div>
                                        <ShieldCheck className="text-nobel-gold shrink-0 relative z-10" size={32} />
                                        <p className="text-base italic font-medium m-0 leading-relaxed text-stone-300 relative z-10">
                                            "GhostPay provides the rails; you own the train. We have zero capacity to reverse or alter on-chain settlements once confirmed. Finality is absolute."
                                        </p>
                                    </div>
                                </LegalSection>

                                <LegalSection id="privacy" title="Privacy Standards" icon={Lock}>
                                    <div className="flex items-center gap-2 text-[10px] font-black text-stone-400 uppercase tracking-[0.4em] mb-10">
                                        <div className="w-8 h-px bg-stone-200 dark:bg-white/10"></div>
                                        Status: Zero-Knowledge
                                    </div>
                                    <p>
                                        The Ghost Protocol is engineered for Absolute Silence. Financial privacy is a fundamental human right. Our framework is based on Privacy-by-Design and strictly enforced Zero-Knowledge standards.
                                    </p>
                                    <div className="grid grid-cols-1 gap-6 md:gap-8 my-16">
                                        <div className="p-8 rounded-sm bg-stone-50 dark:bg-white/[0.03] border border-stone-200 dark:border-white/10 hover:border-nobel-gold/30 transition-all shadow-sm">
                                            <div className="flex items-center gap-4 text-[#0A0B0F] dark:text-white mb-4">
                                                <CheckCircle2 size={20} className="text-emerald-500 shrink-0" />
                                                <span className="font-black text-xs sm:text-sm uppercase tracking-[0.2em]">Identity Isolation</span>
                                            </div>
                                            <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed">No harvest of real-world names, IP addresses, or location telemetry associated with transaction execution. All metadata is decoupled at the runtime level.</p>
                                        </div>
                                        <div className="p-8 rounded-sm bg-stone-50 dark:bg-white/[0.03] border border-stone-200 dark:border-white/10 hover:border-nobel-gold/30 transition-all shadow-sm">
                                            <div className="flex items-center gap-4 text-[#0A0B0F] dark:text-white mb-4">
                                                <CheckCircle2 size={20} className="text-emerald-500 shrink-0" />
                                                <span className="font-black text-xs sm:text-sm uppercase tracking-[0.2em]">Stealth Settlement</span>
                                            </div>
                                            <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed">Automatic generation of one-time, non-interactive addresses for every invoice to prevent transaction correlation and graph analysis by external indexers.</p>
                                        </div>
                                    </div>
                                </LegalSection>

                                <LegalSection id="compliance" title="Audit Registry" icon={FileCheck}>
                                    <div className="bg-[#0A0B0F] rounded-sm p-8 md:p-16 text-white relative overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.8)] border border-white/10">
                                        <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
                                            <Landmark size={320} />
                                        </div>
                                        <div className="relative z-10">
                                            <div className="flex items-center gap-5 mb-12">
                                                <Shield className="text-nobel-gold shrink-0" size={32} />
                                                <h3 className="font-serif text-3xl md:text-4xl font-bold">2026 Security Roadmap</h3>
                                            </div>
                                            <div className="space-y-1 mb-12">
                                                <AuditLine title="SOLANA ANCHOR PROTOCOL" status="PASSED • JAN '26" />
                                                <AuditLine title="SUI MOVE SMART MODULES" status="PASSED • JAN '26" />
                                                <AuditLine title="GHOSTOS ISOLATED RUNTIME" status="PASSED • DEC '25" />
                                            </div>
                                            <button className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.4em] text-nobel-gold hover:text-white transition-all group px-6 py-4 border border-nobel-gold/30 rounded-sm hover:bg-nobel-gold/10">
                                                REQUEST FULL SECURITY LOG <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                            </button>
                                        </div>
                                    </div>
                                </LegalSection>

                                <div className="mt-32 pt-16 border-t border-stone-100 dark:border-white/10 text-center">
                                    <p className="text-[10px] sm:text-xs text-stone-400 font-mono font-bold leading-relaxed px-4 uppercase tracking-[0.4em]">
                                        &copy; 2026 GHOST NETWORK PROTOCOL. SECURED VIA AES-256.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer onLogin={onLogin} onNavigateToDocs={onNavigateToDocs} onNavigate={(p) => onNavigate(p as any)} />
        </div>
    );
};
